'use client';
import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

const Faq = ({
  faqAnswer,
  faqHeading,
  isFirst,
  isWhiteTheme,
}: {
  isWhiteTheme?: boolean;
  faqHeading: React.ReactNode;
  faqAnswer?: React.ReactNode;
  isFirst?: boolean;
}) => {
  const [isQuestionToggled, setIsQuestionToggled] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number>(0);

  useEffect(() => {
    const updateHeight = () => {
      if (contentRef.current) {
        // Get the full height including margins and padding
        const computedStyle = window.getComputedStyle(contentRef.current);
        const marginTop = parseInt(computedStyle.marginTop, 10);
        const marginBottom = parseInt(computedStyle.marginBottom, 10);

        const totalHeight =
          contentRef.current.scrollHeight + marginTop + marginBottom + 10;
        setContentHeight(isQuestionToggled ? totalHeight : 0);
      }
    };

    if (contentRef.current) {
      // Use a small delay to ensure DOM is fully rendered
      const timer = setTimeout(updateHeight, 10);

      const resizeObserver = new ResizeObserver(() => {
        if (isQuestionToggled) {
          updateHeight();
        }
      });

      resizeObserver.observe(contentRef.current);

      return () => {
        clearTimeout(timer);
        resizeObserver.disconnect();
      };
    }
  }, [isQuestionToggled]);

  return (
    <div
      className={classNames(
        'w-full rounded-lg transition-all duration-500 ease-in-out',
        isWhiteTheme ? 'text-white' : 'text-black',
        isQuestionToggled
          ? isWhiteTheme
            ? 'bg-white/5 p-4'
            : 'bg-white p-4'
          : 'bg-transparent'
      )}
    >
      <button
        className={classNames(
          'flex w-full cursor-pointer items-center justify-between border-0 bg-transparent pb-3 text-left transition-all duration-200 ease-in-out hover:opacity-80 focus:outline-none',
          isWhiteTheme ? 'border-b border-white' : 'border-b border-black'
        )}
        onClick={() => setIsQuestionToggled(!isQuestionToggled)}
        aria-expanded={isQuestionToggled}
        aria-controls="faq-content"
      >
        <p
          className={`${isFirst && 'pt-3'} font-['Work_Sans'] text-base leading-5 font-normal tracking-[0%]`}
        >
          {faqHeading}
        </p>
        <svg
          width={20}
          height={20}
          viewBox="0 0 24 24"
          className={classNames(
            'min-h-5 min-w-5 scale-[2] transition-transform duration-300 ease-in-out',
            isQuestionToggled ? 'rotate-180' : 'rotate-0'
          )}
          aria-label={isQuestionToggled ? 'Collapse FAQ' : 'Expand FAQ'}
        >
          <path
            d="M7 10L12 15L17 10Z"
            fill={isWhiteTheme ? 'white' : 'black'}
          />
        </svg>
      </button>

      <div
        id="faq-content"
        className="overflow-hidden transition-all duration-500 ease-in-out"
        style={{
          maxHeight: isQuestionToggled ? `${contentHeight}px` : '0px',
        }}
      >
        <div
          ref={contentRef}
          className={classNames(
            "my-6 mb-12 transform font-['Work_Sans'] text-base leading-[140%] font-normal tracking-[0%] transition-all duration-500 ease-in-out",
            isQuestionToggled
              ? 'translate-y-0 opacity-100'
              : '-translate-y-2 opacity-0'
          )}
        >
          {faqAnswer
            ? faqAnswer
            : 'Lorem ipsum dolor sit amet consectetur. Porttitor sit blandit velit amet at ' +
              'lobortis imperdiet nunc quis. Purus elementum at congue arcu. Venenatis sit amet ' +
              'ornare urna interdum pretium hendrerit. Turpis enim nec consequat purus urna. ' +
              'Purus ipsum sed purus neque eget risus lectus nisl platea. Pharetra sit semper ' +
              'volutpat proin eget justo egestas lectus. Aliquam mi eget sit id risus varius. ' +
              'Massa nascetur at amet dictum. Sed viverra consequat lorem sodales risus.' +
              '\n' +
              'Tempus cursus pellentesque mi sagittis urna velit scelerisque quam congue. Quam ' +
              'duis vel nullam cras nibh eget vitae. In convallis feugiat condimentum ultrices.'}
        </div>
      </div>
    </div>
  );
};

export default Faq;
